import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js'
import { Doughnut, Bar } from 'react-chartjs-2'
import { useAuthStore } from '@/stores/authStore'
import { billingApi } from '@/api/clients/apiClient'

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

function getPeriodOptions() {
  const options: string[] = []
  const now = new Date()
  for (let i = 0; i < 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    // Use local date components — toISOString() would convert to UTC and shift
    // the month for users in UTC+ timezones (e.g. UTC+8 April 1 → March 31 UTC).
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    options.push(`${year}-${month}`)
  }
  return options
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function Usage() {
  const { orgId } = useAuthStore()
  const periodOptions = getPeriodOptions()
  const [period, setPeriod] = useState(periodOptions[0])

  const { data: usageResp, isLoading: loadingUsage, isError: usageError, error: usageErr } = useQuery({
    queryKey: ['usage', orgId, period],
    queryFn: () => billingApi.getUsage(orgId!, period),
    enabled: !!orgId,
  })

  const { data: recordsResp, isLoading: loadingRecords, isError: recordsError } = useQuery({
    queryKey: ['usage-records', orgId, period],
    queryFn: () => billingApi.listRecords(orgId!, period, 50),
    enabled: !!orgId,
  })

  const { data: tiersResp, isLoading: loadingTiers, isError: tiersError } = useQuery({
    queryKey: ['tier-breakdown', orgId, period],
    queryFn: () => billingApi.getTierBreakdown(orgId!, period),
    enabled: !!orgId,
  })

  const isLoading = loadingUsage || loadingRecords || loadingTiers
  const isError = usageError || recordsError || tiersError

  const usage = usageResp?.data
  const records = recordsResp?.data ?? []
  const tiers = tiersResp?.data ?? {}

  const tierNames = Object.keys(tiers)
  const tierColors = [
    'rgba(37,99,235,0.8)',
    'rgba(34,197,94,0.8)',
    'rgba(234,179,8,0.8)',
    'rgba(100,116,139,0.8)',
    'rgba(239,68,68,0.8)',
  ]

  const doughnutData = {
    labels: tierNames.length > 0 ? tierNames : ['No data'],
    datasets: [
      {
        data: tierNames.length > 0 ? tierNames.map((t) => tiers[t].files) : [1],
        backgroundColor: tierNames.length > 0 ? tierColors.slice(0, tierNames.length) : ['rgba(100,116,139,0.3)'],
        borderWidth: 2,
      },
    ],
  }

  const barData = {
    labels: ['Tokens'],
    datasets: [
      {
        label: 'Input',
        data: [usage?.InputTokens ?? 0],
        backgroundColor: 'rgba(37,99,235,0.8)',
        borderRadius: 4,
      },
      {
        label: 'Output',
        data: [usage?.OutputTokens ?? 0],
        backgroundColor: 'rgba(34,197,94,0.8)',
        borderRadius: 4,
      },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { boxWidth: 12, padding: 12, font: { size: 11 } } },
    },
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">Usage</h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            LLM token consumption and costs
          </p>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {periodOptions.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      {/* Error banner */}
      {isError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-3">
          <span className="material-icons text-error text-xl">error_outline</span>
          <p className="text-sm text-red-700 dark:text-red-300">
            Failed to load usage data.{' '}
            {(usageErr as Error)?.message ?? 'Check that the backend is running and your session is valid.'}
          </p>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Input tokens', value: (usage?.InputTokens ?? 0).toLocaleString(), icon: 'arrow_downward' },
          { label: 'Output tokens', value: (usage?.OutputTokens ?? 0).toLocaleString(), icon: 'arrow_upward' },
          {
            label: 'Total cost',
            value: `$${(usage?.CostUSD ?? 0).toFixed(6)}`,
            icon: 'payments',
          },
        ].map((s) => (
          <div
            key={s.label}
            className="bg-surface-light dark:bg-surface-dark p-5 rounded-lg border border-border-light dark:border-border-dark"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="material-icons text-subtle-light dark:text-subtle-dark text-base">
                {s.icon}
              </span>
              <p className="text-xs text-subtle-light dark:text-subtle-dark uppercase tracking-wider">
                {s.label}
              </p>
            </div>
            <p className="text-2xl font-bold text-text-light dark:text-text-dark">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-surface-light dark:bg-surface-dark p-6 rounded-lg border border-border-light dark:border-border-dark">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
            Token Breakdown
          </h2>
          <div className="h-48">
            <Bar
              data={barData}
              options={{
                ...chartOptions,
                scales: {
                  x: { grid: { display: false } },
                  y: { beginAtZero: true, ticks: { font: { size: 10 } } },
                },
              }}
            />
          </div>
        </div>

        <div className="bg-surface-light dark:bg-surface-dark p-6 rounded-lg border border-border-light dark:border-border-dark">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark mb-4">
            Files by Tier
          </h2>
          <div className="h-48">
            {tierNames.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-subtle-light dark:text-subtle-dark">
                <span className="material-icons text-4xl mb-2">pie_chart</span>
                <p className="text-sm">No tier data for this period</p>
              </div>
            ) : (
              <Doughnut data={doughnutData} options={chartOptions} />
            )}
          </div>
        </div>
      </div>

      {/* Usage records table */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-lg border border-border-light dark:border-border-dark overflow-hidden">
        <div className="px-6 py-4 border-b border-border-light dark:border-border-dark">
          <h2 className="text-base font-semibold text-text-light dark:text-text-dark">
            Usage Records
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-light dark:bg-background-dark">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Model
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Tier
                </th>
                <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                  Input
                </th>
                <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                  Output
                </th>
                <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                  Cost
                </th>
                <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                  Files
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Date
                </th>
              </tr>
            </thead>
            <tbody>
              {records.map((r) => (
                <tr
                  key={r.ID}
                  className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                >
                  <td className="px-6 py-3 font-mono text-xs text-text-light dark:text-text-dark">
                    {r.Model}
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark">
                    {r.Tier || '—'}
                  </td>
                  <td className="px-6 py-3 text-right text-text-light dark:text-text-dark">
                    {r.InputTokens.toLocaleString()}
                  </td>
                  <td className="px-6 py-3 text-right text-text-light dark:text-text-dark">
                    {r.OutputTokens.toLocaleString()}
                  </td>
                  <td className="px-6 py-3 text-right font-mono text-xs text-text-light dark:text-text-dark">
                    ${r.CostUSD.toFixed(6)}
                  </td>
                  <td className="px-6 py-3 text-right text-text-light dark:text-text-dark">
                    {r.FilesAnalyzed}
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark text-xs">
                    {formatDate(r.CreatedAt)}
                  </td>
                </tr>
              ))}

              {records.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-8 text-center text-subtle-light dark:text-subtle-dark"
                  >
                    No usage records for {period}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
